import axios from 'axios';

import { authApiClient } from '@/shared/api/core/axios-instance';

import type { UserProfile } from '@/entities/auth';
import { useAuthStore } from '@/entities/auth';

const IS_LOCAL = import.meta.env.DEV;
const IS_DEMO_MODE = import.meta.env.MODE === 'demo';

export type CurrentUserResponse = {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
};

const MOCK_USER_ROLE = import.meta.env.VITE_MOCK_USER_ROLE || 'admin';

const MOCK_USER: UserProfile = {
  username: MOCK_USER_ROLE,
  email: `${MOCK_USER_ROLE}@dev-openmetadata.onepredict.net`,
  firstName: MOCK_USER_ROLE,
  lastName: MOCK_USER_ROLE,
  roles: [MOCK_USER_ROLE],
};

/**
 * 응답이 유효한 사용자 데이터인지 타입 가드로 검증
 */
const isValidUserResponse = (data: unknown): data is CurrentUserResponse => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const user = data as Record<string, unknown>;

  return (
    typeof user.username === 'string' &&
    typeof user.email === 'string' &&
    typeof user.firstName === 'string' &&
    typeof user.lastName === 'string' &&
    Array.isArray(user.roles) &&
    user.roles.every((role) => typeof role === 'string')
  );
};

/**
 * 현재 로그인한 사용자 정보를 백엔드 API로부터 가져옵니다.
 * 개발 환경에서는 목업 사용자 정보를 반환합니다.
 *
 * @returns 사용자 프로필 정보
 * @throws 인증되지 않은 경우 401 에러
 * @throws API 에러 시 에러 throw
 */
export const fetchCurrentUser = async (): Promise<UserProfile> => {
  if (IS_DEMO_MODE) {
    const user = useAuthStore.getState().user;
    if (!user) {
      throw new Error('인증되지 않은 사용자입니다.');
    }

    return user;
  }

  if (IS_LOCAL) {
    return MOCK_USER;
  }

  try {
    const response = await authApiClient.get<CurrentUserResponse>('/backend/api/v1/users/me');

    if (!isValidUserResponse(response.data)) {
      throw new Error('유효하지 않은 사용자 정보 형식입니다.');
    }

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error('인증되지 않은 사용자입니다.');
    }
    console.error('Failed to fetch current user:', error);
    throw error;
  }
};
